import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Button, Chip, Divider, LinearProgress, Tab, Tabs
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  ShoppingCart as OrderIcon,
  CurrencyRupee as RupeeIcon,
  People as PeopleIcon,
  LocalShipping as ShipIcon,
  Factory as FactoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area
} from 'recharts';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Reports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [reports, setReports] = useState({
    products: [],
    orders: [],
    employees: [],
    vehicles: [],
    payments: [],
    expenses: [],
    production: [],
    attendance: []
  });

  const COLORS = ['#4caf50', '#f44336', '#ff9800', '#2196f3', '#9c27b0', '#00bcd4', '#ff5722'];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [
        productsRes,
        ordersRes,
        employeesRes,
        vehiclesRes,
        paymentsRes,
        expensesRes,
        productionRes,
        attendanceRes
      ] = await Promise.all([
        api.get('/inventory/products/'),
        api.get('/sales/orders/'),
        api.get('/hr/employees/'),
        api.get('/dispatch/vehicles/'),
        api.get('/finance/payments/'),
        api.get('/finance/expenses/'),
        api.get('/production/batches/'),
        api.get('/hr/attendance/')
      ]);

      // Process products
      let products = [];
      if (Array.isArray(productsRes.data)) products = productsRes.data;
      else if (productsRes.data?.results) products = productsRes.data.results;

      // Process orders
      let orders = [];
      if (Array.isArray(ordersRes.data)) orders = ordersRes.data;
      else if (ordersRes.data?.results) orders = ordersRes.data.results;

      // Process employees
      let employees = [];
      if (Array.isArray(employeesRes.data)) employees = employeesRes.data;
      else if (employeesRes.data?.results) employees = employeesRes.data.results;

      // Process vehicles
      let vehicles = [];
      if (Array.isArray(vehiclesRes.data)) vehicles = vehiclesRes.data;
      else if (vehiclesRes.data?.results) vehicles = vehiclesRes.data.results;

      // Process payments
      let payments = [];
      if (Array.isArray(paymentsRes.data)) payments = paymentsRes.data;
      else if (paymentsRes.data?.results) payments = paymentsRes.data.results;

      // Process expenses
      let expenses = [];
      if (Array.isArray(expensesRes.data)) expenses = expensesRes.data;
      else if (expensesRes.data?.results) expenses = expensesRes.data.results;

      // Process production
      let production = [];
      if (Array.isArray(productionRes.data)) production = productionRes.data;
      else if (productionRes.data?.results) production = productionRes.data.results;

      // Process attendance
      let attendance = [];
      if (Array.isArray(attendanceRes.data)) attendance = attendanceRes.data;
      else if (attendanceRes.data?.results) attendance = attendanceRes.data.results;

      setReports({
        products,
        orders,
        employees,
        vehicles,
        payments,
        expenses,
        production,
        attendance
      });
    } catch (error) {
      console.error('Error fetching reports data:', error);
      toast.error('Failed to fetch reports data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="subtitle2">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ bgcolor: color + '20', borderRadius: '50%', p: 1.5 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }} align="center">Loading reports...</Typography>
      </Box>
    );
  }

  // Calculate summary stats
  const totalRevenue = reports.payments
    .filter(p => p.payment_type === 'received')
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const totalExpenses = reports.expenses
    .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  const pendingOrders = reports.orders
    .filter(o => o.status === 'draft' || o.status === 'confirmed').length;

  const lowStockItems = reports.products
    .filter(p => parseFloat(p.current_stock || 0) <= parseFloat(p.minimum_stock || 0)).length;

  const totalProduction = reports.production.length;
  const totalAttendance = reports.attendance.length;

  // Order status distribution
  const statusCounts = {};
  reports.orders.forEach(o => {
    statusCounts[o.status] = (statusCounts[o.status] || 0) + 1;
  });
  const statusData = Object.keys(statusCounts).map(key => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: statusCounts[key]
  }));

  // Monthly order data
  const monthlyData = [];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();
  for (let i = 5; i >= 0; i--) {
    const monthIdx = (currentMonth - i + 12) % 12;
    const monthOrders = reports.orders.filter(o => {
      const orderDate = new Date(o.order_date);
      return orderDate.getMonth() === monthIdx;
    });
    monthlyData.push({
      month: months[monthIdx],
      orders: monthOrders.length,
      revenue: monthOrders.reduce((sum, o) => sum + parseFloat(o.net_amount || 0), 0)
    });
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          System Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchAllData}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Products"
            value={reports.products.length}
            icon={<InventoryIcon sx={{ color: '#2E7D32', fontSize: 30 }} />}
            color="#2E7D32"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Orders"
            value={reports.orders.length}
            icon={<OrderIcon sx={{ color: '#1976D2', fontSize: 30 }} />}
            color="#1976D2"
            subtitle={`${pendingOrders} pending`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Revenue"
            value={`₹${totalRevenue.toLocaleString('en-IN')}`}
            icon={<RupeeIcon sx={{ color: '#FF8F00', fontSize: 30 }} />}
            color="#FF8F00"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Expenses"
            value={`₹${totalExpenses.toLocaleString('en-IN')}`}
            icon={<TrendingDownIcon sx={{ color: '#D32F2F', fontSize: 30 }} />}
            color="#D32F2F"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Employees"
            value={reports.employees.length}
            icon={<PeopleIcon sx={{ color: '#9C27B0', fontSize: 30 }} />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Low Stock"
            value={lowStockItems}
            icon={<WarningIcon sx={{ color: '#D32F2F', fontSize: 30 }} />}
            color="#D32F2F"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Orders & Revenue Trend
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="orders" stroke="#1976D2" name="Orders" />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#FF8F00" name="Revenue (₹)" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Order Status
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Reports
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate('/attendance-reports')}
              sx={{ py: 2 }}
            >
              Attendance Reports
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => navigate('/inventory')}
              sx={{ py: 2 }}
            >
              Inventory Reports
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => navigate('/sales')}
              sx={{ py: 2 }}
            >
              Sales Reports
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="info"
              fullWidth
              onClick={() => navigate('/finance')}
              sx={{ py: 2 }}
            >
              Financial Reports
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Reports;